// ds-allow-file: canvas drawing; gradient stops are rgba() literals mixed per frame
import React, { useEffect, useRef } from "react";
import { cn } from "@src/lib/utils/cn";
import {
  ORB_MAX_RADIUS_SHARE,
  orbClouds,
  orbFrame,
  orbStyle,
  orbTarget,
  smoothLevel,
} from "./orb";
import type { VoiceStatus } from "./useOpusAgentVoice";

const POINTS = 96;

// The live voice orb: a soft glowing blob that swells with whoever is talking
// (the admin while listening, the voice while speaking), with soft light
// clouds drifting inside that race while Opus Agent works. One canvas, redrawn
// per animation frame.
export const VoiceOrb: React.FC<{
  status: VoiceStatus;
  muted: boolean;
  getInputLevel: () => number;
  getOutputLevel: () => number;
  size?: number;
  className?: string;
}> = ({
  status,
  muted,
  getInputLevel,
  getOutputLevel,
  size = 144,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Latest props for the animation loop, which starts once per mount.
  const propsRef = useRef({ status, muted, getInputLevel, getOutputLevel });
  propsRef.current = { status, muted, getInputLevel, getOutputLevel };

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    // Queried per frame so a setting change applies mid-call.
    const motionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    let level = 0;
    let frame = 0;
    const started = performance.now();

    const draw = (now: number) => {
      const { status, muted, getInputLevel, getOutputLevel } = propsRef.current;
      const ratio = window.devicePixelRatio || 1;
      const pixels = Math.round(size * ratio);
      if (canvas.width !== pixels || canvas.height !== pixels) {
        canvas.width = pixels;
        canvas.height = pixels;
      }
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, size, size);

      const reducedMotion = !!motionQuery?.matches;
      level = smoothLevel(
        level,
        orbTarget(status, muted, getInputLevel, getOutputLevel),
      );
      const style = orbStyle(status, muted);
      const { palette } = style;
      const center = size / 2;
      // Leave room for the halo around the largest body.
      const { radius, wobbleAt } = orbFrame(
        style,
        level,
        (now - started) / 1000,
        size * ORB_MAX_RADIUS_SHARE,
        reducedMotion,
      );

      // Halo.
      const halo = context.createRadialGradient(
        center,
        center,
        radius * 0.6,
        center,
        center,
        radius * (1.35 + level * 0.25),
      );
      halo.addColorStop(0, palette.glow);
      halo.addColorStop(1, "rgba(255, 255, 255, 0)");
      context.fillStyle = halo;
      context.fillRect(0, 0, size, size);

      // Body outline.
      context.beginPath();
      for (let point = 0; point <= POINTS; point++) {
        const theta = (point / POINTS) * Math.PI * 2;
        const r = radius * (1 + wobbleAt(theta));
        const x = center + r * Math.cos(theta);
        const y = center + r * Math.sin(theta);
        if (point === 0) context.moveTo(x, y);
        else context.lineTo(x, y);
      }
      context.closePath();

      context.save();
      context.clip();
      // Body, lit from the upper left.
      const body = context.createRadialGradient(
        center - radius * 0.35,
        center - radius * 0.4,
        radius * 0.1,
        center,
        center,
        radius * 1.15,
      );
      body.addColorStop(0, palette.core);
      body.addColorStop(0.45, palette.mid);
      body.addColorStop(1, palette.edge);
      context.fillStyle = body;
      context.fillRect(0, 0, size, size);

      // Clouds: soft light drifting inside the body.
      for (const cloud of orbClouds(
        style,
        level,
        (now - started) / 1000,
        center,
        radius,
        reducedMotion,
      )) {
        const puff = context.createRadialGradient(
          cloud.x,
          cloud.y,
          0,
          cloud.x,
          cloud.y,
          cloud.r,
        );
        puff.addColorStop(0, cloud.color);
        puff.addColorStop(1, "rgba(0, 0, 0, 0)");
        context.globalCompositeOperation = cloud.blend;
        context.fillStyle = puff;
        context.fillRect(0, 0, size, size);
      }
      context.globalCompositeOperation = "source-over";

      // Specular highlight.
      const shine = context.createRadialGradient(
        center - radius * 0.38,
        center - radius * 0.45,
        0,
        center - radius * 0.38,
        center - radius * 0.45,
        radius * 0.7,
      );
      shine.addColorStop(0, "rgba(255, 255, 255, 0.75)");
      shine.addColorStop(1, "rgba(255, 255, 255, 0)");
      context.fillStyle = shine;
      context.fillRect(0, 0, size, size);
      context.restore();

      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [size]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      data-testid="opus-agent-voice-orb"
      data-status={status}
      style={{ width: size, height: size }}
      className={cn("shrink-0", className)}
    />
  );
};
